import {
  BadgeDollarSign,
  Banknote,
  BookOpenCheck,
  BriefcaseBusiness,
  Building2,
  ChartNoAxesCombined,
  ChevronLeft,
  CircleDollarSign,
  ClipboardCheck,
  FileCheck2,
  FileClock,
  FolderKanban,
  HandCoins,
  History,
  Home,
  KeyRound,
  Landmark,
  LayoutDashboard,
  LogOut,
  Menu,
  PlusSquare,
  Radio,
  ReceiptText,
  Settings,
  ShieldCheck,
  Tags,
  UserCheck,
  UserRound,
  UsersRound,
  WalletCards,
  X,
} from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../../features/auth/AuthContext.jsx'
import { hasPermission, hasAnyPermission } from '../../lib/permissions.js'
import { useFocusTrap } from '../../hooks/useFocusTrap.js'
import { BrandMark } from '../ui/BrandMark.jsx'
import { Badge } from '../ui/Badge.jsx'

const menus = {
  CREATOR: [
    { label: 'Home', to: '/creator', icon: Home, end: true },
    { label: 'Campaign', to: '/explore', icon: FolderKanban },
    { label: 'Pekerjaan', to: '/creator/jobs', icon: BriefcaseBusiness },
    { label: 'Saldo', to: '/creator/wallet', icon: WalletCards },
    { label: 'Profil', to: '/creator/profile', icon: UserRound },
  ],
  BRAND: [
    { label: 'Dashboard', to: '/brand', icon: LayoutDashboard, end: true },
    { label: 'Campaign Saya', to: '/brand/campaigns', icon: FolderKanban, end: true },
    { label: 'Buat Campaign', to: '/brand/campaigns/new', icon: PlusSquare, end: true },
    { label: 'Applications', to: '/brand/applications', icon: UsersRound },
    { label: 'Keuangan', to: '/brand/finance', icon: CircleDollarSign },
    { label: 'Profil Brand', to: '/brand/profile', icon: Building2 },
  ],
}

const superAdminMenuGroup = {
  group: 'Super Admin',
  badge: 'Super',
  items: [
    { label: 'Manajemen Admin', to: '/admin/superadmin', icon: ShieldCheck, end: true, permission: 'superadmin.manage' },
    { label: 'Manajemen Role', to: '/admin/roles', icon: KeyRound, end: true, permission: 'rbac.manage' },
  ],
}

const adminMenuGroups = [
  {
    group: 'Pusat Operasional',
    badge: null,
    items: [
      { label: 'Dashboard', to: '/admin', icon: LayoutDashboard, end: true },
      { label: 'Laporan Analitik', to: '/admin/reports', icon: ChartNoAxesCombined, end: true, anyOf: ['revenue.read', 'finance.read', 'report.executive.read'] },
    ],
  },
  {
    group: 'Menu Brand',
    badge: 'Brand',
    items: [
      { label: 'Daftar Brand', to: '/admin/brands', icon: Building2, end: true, permission: 'brand.read' },
      { label: 'Semua Campaign', to: '/admin/campaigns', icon: FolderKanban, end: true, permission: 'campaign.review' },
      { label: 'Review & Publish', to: '/admin/reviews', icon: ClipboardCheck, end: true, permission: 'campaign.review' },
      { label: 'Saldo Dompet Brand', to: '/admin/brand-wallets', icon: WalletCards, end: true, permission: 'brand.wallet.read' },
    ],
  },
  {
    group: 'Menu Creator',
    badge: 'Creator',
    items: [
      { label: 'Validasi Creator', to: '/admin/creator-reviews', icon: UserCheck, end: true, permission: 'creator.read' },
      { label: 'Daftar Creator', to: '/admin/creators', icon: UserRound, end: true, permission: 'creator.read' },
      { label: 'Pelamar Campaign', to: '/admin/applications', icon: BriefcaseBusiness, end: true, permission: 'creator.read' },
      { label: 'Submission Video', to: '/admin/submissions', icon: FileClock, end: true, permission: 'submission.validate' },
      { label: 'Validasi View', to: '/admin/validations', icon: FileCheck2, end: true, permission: 'submission.validate' },
      { label: 'Penghasilan (Earnings)', to: '/admin/earnings', icon: BadgeDollarSign, end: true, anyOf: ['finance.read', 'ledger.read'] },
      { label: 'Pencairan (Withdrawal)', to: '/admin/withdrawals', icon: Banknote, end: true, permission: 'withdrawal.process' },
    ],
  },
  {
    group: 'Sistem & Platform',
    badge: 'Umum',
    items: [
      { label: 'Broadcast WhatsApp', to: '/admin/broadcasts', icon: Radio, end: false, anyOf: ['broadcast.read', 'broadcast.manage'] },
      { label: 'Buku Besar (Ledger)', to: '/admin/ledger', icon: ReceiptText, end: true, permission: 'ledger.read' },
      { label: 'Rekening Plotfy', to: '/admin/bank-accounts', icon: Landmark, end: true, anyOf: ['platform.bank-account.manage', 'finance.read'] },
      { label: 'Kategori Campaign', to: '/admin/categories', icon: Tags, end: true, permission: 'category.manage' },
      { label: 'Audit Log', to: '/admin/audit', icon: History, end: true, permission: 'audit.read' },
      { label: 'Pengaturan Platform', to: '/admin/settings', icon: Settings, end: true, permission: 'settings.read' },
    ],
  },
]

const mobileMenus = {
  CREATOR: menus.CREATOR,
  BRAND: [menus.BRAND[0], menus.BRAND[1], menus.BRAND[2], menus.BRAND[3], menus.BRAND[5]],
  ADMIN: [
    { label: 'Dashboard', to: '/admin', icon: LayoutDashboard, end: true },
    { label: 'Review', to: '/admin/reviews', icon: ClipboardCheck },
    { label: 'Validasi', to: '/admin/validations', icon: FileCheck2 },
    { label: 'Withdrawal', to: '/admin/withdrawals', icon: Banknote },
  ],
  SUPERADMIN: [
    { label: 'Dashboard', to: '/admin', icon: LayoutDashboard, end: true },
    { label: 'Admin', to: '/admin/superadmin', icon: ShieldCheck },
    { label: 'Review', to: '/admin/reviews', icon: ClipboardCheck },
    { label: 'Validasi', to: '/admin/validations', icon: FileCheck2 },
  ],
}

function SidebarContent({ role, onNavigate, onLogout }) {
  const { user } = useAuth()
  const currentRole = String(role || user?.role || 'CREATOR').toUpperCase()
  const isSuperAdmin = user?.role === 'SUPERADMIN' || currentRole === 'SUPERADMIN'

  if (currentRole === 'ADMIN' || currentRole === 'SUPERADMIN') {
    const rawGroups = isSuperAdmin ? [superAdminMenuGroup, ...adminMenuGroups] : adminMenuGroups
    const groups = rawGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (item.permission && !hasPermission(user, item.permission)) return false
          if (item.anyOf && !hasAnyPermission(user, item.anyOf)) return false
          return true
        }),
      }))
      .filter((group) => group.items.length > 0)

    return (
      <>
        <div className="flex h-[70px] shrink-0 items-center border-b border-line px-5">
          <BrandMark />
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {groups.map((group) => (
            <div key={group.group}>
              <div className="mb-2 flex items-center justify-between px-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-copy-disabled">
                  {group.group}
                </span>
                {group.badge && (
                  <span className={`rounded-md px-1.5 py-0.5 text-[9px] font-extrabold border shadow-2xs ${
                    group.badge === 'Super'
                      ? 'bg-purple-50 text-purple-900 border-purple-300'
                      : group.badge === 'Brand'
                        ? 'bg-amber-50 text-amber-900 border-amber-300'
                        : group.badge === 'Creator'
                          ? 'bg-emerald-50 text-emerald-900 border-emerald-300'
                          : 'bg-slate-100 text-slate-800 border-slate-300'
                  }`}>
                    {group.badge}
                  </span>
                )}
              </div>
              <nav className="space-y-0.5" aria-label={group.group}>
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      `flex min-h-10 items-center gap-3 rounded-[10px] px-3 text-xs font-medium transition-colors ${
                        isActive
                          ? 'bg-brand-600 text-white font-semibold shadow-sm'
                          : 'text-copy-muted hover:bg-ink-800 hover:text-copy'
                      }`
                    }
                  >
                    <item.icon className="size-[17px] shrink-0" aria-hidden="true" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                ))}
              </nav>
            </div>
          ))}
        </div>
        <div className="shrink-0 border-t border-line p-3">
          <button
            type="button"
            onClick={onLogout}
            className="flex min-h-10 w-full items-center gap-3 rounded-[10px] px-3 text-xs font-semibold text-copy-muted hover:bg-rose-50 hover:text-rose-700 transition-colors"
          >
            <LogOut className="size-[17px]" aria-hidden="true" /> Keluar
          </button>
        </div>
      </>
    )
  }

  const roleMenus = menus[currentRole] || menus.CREATOR || []

  return (
    <>
      <div className="flex h-[70px] shrink-0 items-center border-b border-line px-5">
        <BrandMark />
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-4">
        <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.14em] text-copy-disabled">
          {currentRole === 'BRAND' ? 'Ruang Brand' : 'Ruang Creator'}
        </p>
        <nav className="space-y-1" aria-label={`Navigasi ${currentRole.toLowerCase()}`}>
          {roleMenus.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex min-h-11 items-center gap-3 rounded-[11px] px-3 text-sm font-medium transition-colors ${
                  isActive ? 'bg-brand-600 text-white' : 'text-copy-muted hover:bg-ink-800 hover:text-copy'
                }`
              }
            >
              <item.icon className="size-[18px] shrink-0" aria-hidden="true" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
      <div className="shrink-0 border-t border-line p-3">
        <button
          type="button"
          onClick={onLogout}
          className="flex min-h-11 w-full items-center gap-3 rounded-[11px] px-3 text-sm font-semibold text-copy-muted hover:bg-rose-50 hover:text-rose-700 transition-colors"
        >
          <LogOut className="size-[18px]" aria-hidden="true" /> Keluar
        </button>
      </div>
    </>
  )
}

export function DashboardLayout() {
  const { user, logout } = useAuth()
  if (user?.status === 'SUSPENDED') {
    return <Navigate to="/account-suspended" replace />
  }

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const sidebarRef = useRef(null)
  const location = useLocation()
  const closeSidebar = useCallback(() => setSidebarOpen(false), [])
  useFocusTrap(sidebarRef, sidebarOpen, closeSidebar)

  const role = String(user?.role || 'CREATOR').toUpperCase()
  const mobileItems = mobileMenus[role] || mobileMenus.CREATOR || []

  async function handleLogout() {
    await logout()
    window.location.assign('/')
  }

  return (
    <div className="min-h-screen bg-ink-950">
      <a
        href="#dashboard-content"
        className="fixed left-4 top-3 z-[100] -translate-y-20 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-transform focus:translate-y-0"
      >
        Lewati ke konten
      </a>
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[260px] flex-col border-r border-line bg-ink-900 lg:flex">
        <SidebarContent role={role} onLogout={handleLogout} />
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-[70] bg-black/75 lg:hidden"
          onMouseDown={(event) => event.target === event.currentTarget && closeSidebar()}
        >
          <aside
            ref={sidebarRef}
            role="dialog"
            aria-modal="true"
            aria-label="Menu dashboard"
            className="flex h-full w-[min(86vw,320px)] flex-col border-r border-line-strong bg-ink-900 shadow-2xl"
          >
            <button
              type="button"
              onClick={closeSidebar}
              className="absolute left-[min(calc(86vw-52px),268px)] top-3 z-10 grid size-11 place-items-center rounded-[11px] text-copy-muted hover:bg-ink-800 hover:text-copy"
              aria-label="Tutup menu"
            >
              <X className="size-5" aria-hidden="true" />
            </button>
            <SidebarContent role={role} onNavigate={closeSidebar} onLogout={handleLogout} />
          </aside>
        </div>
      )}

      <div className="lg:pl-[260px]">
        <header className="sticky top-0 z-30 flex h-[64px] items-center justify-between gap-3 border-b border-line bg-ink-950/95 px-4 backdrop-blur-md sm:px-6 lg:h-[70px] lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="grid size-11 shrink-0 place-items-center rounded-[11px] border border-line bg-ink-900 text-copy lg:hidden"
              aria-label="Buka menu dashboard"
              aria-expanded={sidebarOpen}
            >
              <Menu className="size-5" aria-hidden="true" />
            </button>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-copy">{user?.name}</p>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-copy-muted">
                <span>{role === 'CREATOR' ? 'Creator' : role === 'BRAND' ? 'Brand' : role === 'SUPERADMIN' ? 'Super Administrator' : 'Administrator'}</span>
                {user?.demo && <Badge>Demo</Badge>}
              </div>
            </div>
          </div>
          <NavLink
            to="/"
            className="hidden min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium text-copy-muted hover:bg-ink-850 hover:text-copy sm:flex"
          >
            <ChevronLeft className="size-4" aria-hidden="true" /> Situs publik
          </NavLink>
          <span
            className="grid size-10 shrink-0 place-items-center rounded-full border border-line-strong bg-ink-800 text-sm font-bold text-brand-300 sm:hidden"
            aria-hidden="true"
          >
            {user?.name?.charAt(0)?.toUpperCase() || 'P'}
          </span>
        </header>

        <main
          id="dashboard-content"
          className="min-h-[calc(100vh-64px)] px-4 py-6 pb-28 sm:px-6 lg:min-h-[calc(100vh-70px)] lg:px-8 lg:py-8 lg:pb-8"
        >
          <div className="mx-auto w-full max-w-[1280px]" key={location.pathname}>
            {user?.demo && (
              <div className="mb-5 flex items-center gap-2.5 rounded-xl border border-brand-500/30 bg-brand-500/10 px-3.5 py-2.5 text-xs font-medium text-brand-700">
                <ShieldCheck className="size-4 shrink-0 text-brand-600" aria-hidden="true" />
                Mode demo aktif. Perubahan hanya ditampilkan pada perangkat ini.
              </div>
            )}
            <Outlet />
          </div>
        </main>
      </div>

      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-ink-900/98 px-1.5 pb-[max(6px,env(safe-area-inset-bottom))] pt-1.5 shadow-[0_-8px_28px_rgba(0,0,0,.28)] lg:hidden"
        aria-label="Navigasi dashboard seluler"
      >
        <div className="mx-auto flex max-w-lg items-stretch justify-around">
          {mobileItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex min-h-[56px] min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-[10px] px-1 text-[10px] font-medium transition-colors ${
                  isActive ? 'bg-brand-600/15 text-brand-300' : 'text-copy-disabled hover:text-copy-secondary'
                }`
              }
            >
              <item.icon className="size-[19px]" aria-hidden="true" />
              <span className="max-w-full truncate">{item.label.replace(' Saya', '')}</span>
            </NavLink>
          ))}
          {role === 'ADMIN' && (
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="flex min-h-[56px] min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-[10px] px-1 text-[10px] font-medium text-copy-disabled hover:text-copy-secondary"
            >
              <Menu className="size-[19px]" aria-hidden="true" />
              <span>Semua Menu</span>
            </button>
          )}
        </div>
      </nav>
    </div>
  )
}
